import { useEffect, useState, useCallback } from "react";
import { Plus, Info } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import DoseForm from "../forms/DoseForm";
import RecordActions from "../components/RecordActions";

const PURP_LABEL = { basal: "Basal", meal_correction: "Refeição / correção", unspecified: "Não informada" };

function Stat({ label, value, unit }) {
  return (
    <div className="df-card p-4 min-w-0">
      <div className="text-xs font-semibold text-[#627D98] uppercase break-words">{label}</div>
      <div className="mt-1 font-head text-2xl font-bold text-[#0D253F]">{value}{unit && <span className="text-sm text-[#627D98] ml-1">{unit}</span>}</div>
    </div>
  );
}

export default function Doses() {
  const { t } = useI18n();
  const { activeProfileId, can } = useProfile();
  const [summary, setSummary] = useState(null);
  const [doses, setDoses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    if (!activeProfileId) return;
    const [s, d] = await Promise.all([
      api.get(`/profiles/${activeProfileId}/doses/daily`),
      api.get(`/profiles/${activeProfileId}/doses`),
    ]);
    setSummary(s.data);
    setDoses(d.data);
  }, [activeProfileId]);

  useEffect(() => { load(); }, [load]);

  async function confirmDay(checked) {
    const day = new Date().toISOString().slice(0, 10);
    await api.post(`/profiles/${activeProfileId}/doses/confirm`, { day, confirmed: checked });
    load();
  }

  if (!summary) return <div className="text-[#627D98]">{t("loading")}</div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-head text-2xl sm:text-3xl font-bold text-[#0D253F]">{t("doses_title")}</h1>
          <p className="text-[#627D98] mt-1">{t("doses_sub")}</p>
        </div>
        {can("log_doses") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold shrink-0" data-testid="register-dose-btn">
                <Plus size={18} className="mr-1" /> {t("register_dose")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-head text-xl">{t("register_dose")}</DialogTitle></DialogHeader>
              <DoseForm onSaved={() => { setOpen(false); load(); }} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={t("total_today")} value={summary.total} unit={t("units")} />
        <Stat label={t("subtotal_basal")} value={summary.basal} unit={t("units")} />
        <Stat label={t("subtotal_meal")} value={summary.meal_correction} unit={t("units")} />
        <Stat label={t("u_per_kg")} value={summary.u_per_kg != null ? summary.u_per_kg : "—"} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="df-card p-5">
          <h3 className="font-head font-semibold text-[#0D253F] mb-1">{t("u_per_kg")}</h3>
          {summary.u_per_kg != null ? (
            <div className="text-sm text-[#3B526B] space-y-1 mt-2">
              <div>{t("total_today")}: <strong>{summary.total} {t("units")}</strong></div>
              <div>{t("weight_used")}: <strong>{summary.weight} kg</strong></div>
              <div>{t("weight_date")}: <strong>{summary.weight_date}</strong></div>
            </div>
          ) : (
            <p className="text-sm text-[#627D98] mt-2">{t("no_weight")}</p>
          )}
          <p className="text-xs text-[#627D98] mt-3 flex items-start gap-1.5"><Info size={13} className="mt-0.5" /> {t("descriptive_note")}</p>
        </div>

        <div className="df-card p-5">
          <h3 className="font-head font-semibold text-[#0D253F] mb-2">{t("applications")} · {summary.count}</h3>
          <label className="flex items-center gap-2 text-sm text-[#3B526B] cursor-pointer">
            <Checkbox checked={summary.confirmed} onCheckedChange={confirmDay} disabled={!can("log_doses")} data-testid="confirm-day" />
            {summary.confirmed ? t("record_confirmed") : t("confirm_complete")}
          </label>
          <p className="text-xs text-[#627D98] mt-3">{t("day_not_zero")}</p>
          <p className="text-xs text-[#627D98] mt-1">{t("not_sum_suggestions")}</p>
        </div>
      </div>

      <div className="df-card p-5">
        {doses.length === 0 ? (
          <p className="text-sm text-[#627D98]">{t("no_doses")}</p>
        ) : (
          <>
          <div className="sm:hidden divide-y divide-[#F0F5FA]" data-testid="doses-mobile-list">
            {doses.map((d) => (
              <div key={d.id} className="py-3 space-y-1" data-testid={`dose-mrow-${d.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate">
                    <span className="font-semibold text-[#0D253F]">{d.units} {t("units")}</span>
                    <span className="text-[#0D253F]"> · {d.insulin_name || "—"}</span>
                  </span>
                  <RecordActions record={d} canEdit={can("correct_records")} onEdit={setEditRow} testid={`dose-m-${d.id}`} />
                </div>
                <div className="text-xs text-[#627D98] truncate">
                  {new Date(d.datetime).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  {" · "}{PURP_LABEL[d.purpose]}{" · "}{d.author_name}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden sm:block overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#627D98] text-xs uppercase">
                  <th className="py-2 pr-4">{t("g_table_dt")}</th>
                  <th className="py-2 pr-4">{t("d_insulin")}</th>
                  <th className="py-2 pr-4">{t("d_purpose")}</th>
                  <th className="py-2 pr-4">{t("d_units")}</th>
                  <th className="py-2">{t("g_table_author")}</th>
                  <th className="py-2 text-right">{t("actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F5FA]">
                {doses.map((d) => (
                  <tr key={d.id} data-testid={`dose-row-${d.id}`}>
                    <td className="py-2.5 pr-4 text-[#3B526B]">{new Date(d.datetime).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="py-2.5 pr-4 text-[#0D253F]">{d.insulin_name || "—"}</td>
                    <td className="py-2.5 pr-4 text-[#3B526B]">{PURP_LABEL[d.purpose]}</td>
                    <td className="py-2.5 pr-4 font-semibold text-[#0D253F]">{d.units} {t("units")}</td>
                    <td className="py-2.5 text-[#627D98]">{d.author_name}</td>
                    <td className="py-2.5 text-right"><RecordActions record={d} canEdit={can("correct_records")} onEdit={setEditRow} testid={`dose-${d.id}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {editRow && (
        <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-head text-xl">{t("edit")} · {t("doses_title")}</DialogTitle></DialogHeader>
            <DoseForm record={editRow} onSaved={() => { setEditRow(null); load(); }} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
